/**
 * EveryINC - User Modal Helpers Module
 * User modal form utilities
 */

import type { User, UserFormPayload, UserModalElements, UserFormFieldMap, UserRole } from './types';

interface ApplyModalStateOptions {
  isEditing: boolean;
}

function applyUserModalState(
  modalElements: UserModalElements,
  options: ApplyModalStateOptions
): void {
  const { form, title, passwordInput, submitText } = modalElements;
  const { isEditing } = options;

  if (form) {
    form.reset();
    form.dataset.mode = isEditing ? 'edit' : 'create';
  }

  if (title) {
    title.textContent = isEditing ? 'Editar Usuario' : 'Novo Usuario';
  }

  if (submitText) {
    submitText.textContent = isEditing ? 'Salvar Usuario' : 'Criar Usuario';
  }

  if (passwordInput) {
    passwordInput.required = !isEditing;
    passwordInput.placeholder = isEditing
      ? 'Deixe em branco para manter a senha atual'
      : 'Senha temporaria';
  }
}

function resetUserModalState(modalElements: UserModalElements): void {
  const { form, passwordInput, submitText } = modalElements;

  if (form) {
    delete form.dataset.mode;
  }

  if (submitText) {
    submitText.textContent = 'Criar Usuario';
  }

  if (passwordInput) {
    passwordInput.required = true;
    passwordInput.placeholder = 'Senha temporaria';
  }
}

function syncOwnerOnlyOptions(currentUserRole: UserRole | string | undefined | null): void {
  const ownerOptions = document.querySelectorAll<HTMLElement>('.owner-only');
  ownerOptions.forEach(option => {
    option.style.display = currentUserRole === 'owner' ? 'block' : 'none';
  });

  // Dashboard access section only visible for owner and admin
  const dashboardSection = document.getElementById('dashboard-access-section');
  if (dashboardSection) {
    dashboardSection.style.display = (currentUserRole === 'owner' || currentUserRole === 'admin') ? 'block' : 'none';
  }
}

let dashboardCheckTimeout: ReturnType<typeof setTimeout> | null = null;

async function checkDashboardAccess(email: string): Promise<void> {
  const statusEl = document.getElementById('dashboard-access-status');
  if (!statusEl) return;

  if (!email || !email.includes('@')) {
    statusEl.className = 'dashboard-access-status';
    statusEl.innerHTML = '<span class="status-text">Digite o email para verificar</span>';
    return;
  }

  // Show checking state
  statusEl.className = 'dashboard-access-status checking';
  statusEl.innerHTML = `
    <svg class="status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
    <span class="status-text">Verificando...</span>
  `;

  try {
    const token = localStorage.getItem('inc_token') || sessionStorage.getItem('inc_token');
    const response = await fetch('/api/dashboard/check-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ email })
    });

    const data = await response.json() as { exists?: boolean; inactive?: boolean; error?: boolean; message?: string };

    if (data.exists && !data.inactive) {
      statusEl.className = 'dashboard-access-status available';
      statusEl.innerHTML = `
        <svg class="status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        <span class="status-text">Usuário tem acesso ao Dashboard</span>
      `;
    } else if (data.inactive) {
      statusEl.className = 'dashboard-access-status unavailable';
      statusEl.innerHTML = `
        <svg class="status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <span class="status-text">Usuário inativo no Dashboard</span>
      `;
    } else if (data.error) {
      statusEl.className = 'dashboard-access-status error';
      statusEl.innerHTML = `
        <svg class="status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        <span class="status-text">Dashboard não disponível</span>
      `;
    } else {
      statusEl.className = 'dashboard-access-status unavailable';
      statusEl.innerHTML = `
        <svg class="status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        <span class="status-text">Usuário não cadastrado no Dashboard</span>
      `;
    }
  } catch {
    statusEl.className = 'dashboard-access-status error';
    statusEl.innerHTML = `
      <svg class="status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
      <span class="status-text">Erro ao verificar</span>
    `;
  }
}

function setupDashboardEmailCheck(): void {
  const emailInput = document.getElementById('user-email') as HTMLInputElement | null;
  if (!emailInput) return;

  emailInput.addEventListener('input', () => {
    if (dashboardCheckTimeout) clearTimeout(dashboardCheckTimeout);
    dashboardCheckTimeout = setTimeout(() => {
      void checkDashboardAccess(emailInput.value);
    }, 500);
  });

  emailInput.addEventListener('blur', () => {
    if (dashboardCheckTimeout) clearTimeout(dashboardCheckTimeout);
    void checkDashboardAccess(emailInput.value);
  });
}

function populateUserForm(selectedUser: User, fieldMap: UserFormFieldMap): void {
  const {
    firstNameInput,
    lastNameInput,
    emailInput,
    roleSelect,
    companySelect,
    departmentInput,
    passwordInput,
    submitText
  } = fieldMap;

  if (firstNameInput) firstNameInput.value = selectedUser.first_name || '';
  if (lastNameInput) lastNameInput.value = selectedUser.last_name || '';
  if (emailInput) emailInput.value = selectedUser.email || '';
  if (roleSelect) roleSelect.value = selectedUser.role || '';
  if (departmentInput) departmentInput.value = selectedUser.department || '';
  if (submitText) submitText.textContent = 'Salvar Usuario';

  if (passwordInput) {
    passwordInput.value = '';
    passwordInput.required = false;
    passwordInput.placeholder = 'Deixe em branco para manter a senha atual';
  }

  if (companySelect) {
    companySelect.value = selectedUser.company_id ? String(selectedUser.company_id) : '';
  }
}

interface BuildPayloadOptions {
  includePassword: boolean;
}

function buildUserFormPayload(
  doc: Document,
  options: BuildPayloadOptions
): UserFormPayload {
  const { includePassword } = options;

  const firstNameInput = doc.getElementById('user-first-name') as HTMLInputElement | null;
  const lastNameInput = doc.getElementById('user-last-name') as HTMLInputElement | null;
  const emailInput = doc.getElementById('user-email') as HTMLInputElement | null;
  const roleSelect = doc.getElementById('user-role') as HTMLSelectElement | null;
  const companySelect = doc.getElementById('user-company') as HTMLSelectElement | null;
  const departmentInput = doc.getElementById('user-department') as HTMLInputElement | null;
  const passwordInput = doc.getElementById('user-password') as HTMLInputElement | null;

  const payload: UserFormPayload = {
    first_name: firstNameInput?.value || '',
    last_name: lastNameInput?.value || '',
    email: emailInput?.value || '',
    role: (roleSelect?.value as UserRole) || 'user',
    company_id: parseInt(companySelect?.value || '', 10) || null,
    department: departmentInput?.value || null
  };

  if (includePassword && passwordInput?.value) {
    payload.password = passwordInput.value;
  }

  return payload;
}

const EveryIncUserModalHelpers = {
  applyUserModalState,
  resetUserModalState,
  syncOwnerOnlyOptions,
  populateUserForm,
  buildUserFormPayload,
  checkDashboardAccess,
  setupDashboardEmailCheck
};

// Attach to window for IIFE compatibility
(window as Window).EveryIncUserModalHelpers = EveryIncUserModalHelpers;

export {
  applyUserModalState,
  resetUserModalState,
  syncOwnerOnlyOptions,
  populateUserForm,
  buildUserFormPayload,
  checkDashboardAccess,
  setupDashboardEmailCheck
};

export default EveryIncUserModalHelpers;
